import { useCallback, useMemo } from 'react';
import {
  addWeeks,
  addYears,
  eachDayOfInterval,
  endOfWeek,
  isAfter,
  isBefore,
  startOfDay,
  startOfWeek,
  subWeeks,
} from 'date-fns';

import { useTwoWeekViewState, useWeekViewActions, useWeekViewState } from 'stores/dischargeCalendarStore';

import useSnfLaunchDate from './useSnfLaunchDate';

interface UseWeekNavigationOptions {
  weeks?: 1 | 2;
}

export interface UseWeekNavigationResult {
  startDate: Date;
  endDate: Date;
  daysOfWeek: Date[];
  goToNextWeek: () => void;
  goToPreviousWeek: () => void;
  goToToday: () => void;
  canGoNext: boolean;
  canGoPrevious: boolean;
  isCurrentWeek: boolean;
}

export default function useWeekNavigation({ weeks = 1 }: UseWeekNavigationOptions = {}): UseWeekNavigationResult {
  const weekView = useWeekViewState();
  const twoWeekView = useTwoWeekViewState();
  const { startDate, endDate } = weeks === 2 ? twoWeekView : weekView;
  const actions = useWeekViewActions();
  const snfLaunchDate = useSnfLaunchDate();

  const daysOfWeek = useMemo(() => eachDayOfInterval({ start: startDate, end: endDate }), [startDate, endDate]);

  const todayWeekStart = useMemo(() => startOfWeek(new Date(), { weekStartsOn: 0 }), []);

  const isCurrentWeek = useMemo(() => startDate.getTime() === todayWeekStart.getTime(), [startDate, todayWeekStart]);

  const canGoPrevious = useMemo(() => {
    if (!snfLaunchDate) return true;
    const previousWeekStart = subWeeks(startDate, 1);
    const previousWeekEnd = endOfWeek(previousWeekStart, { weekStartsOn: 0 });
    return !isBefore(previousWeekEnd, startOfDay(snfLaunchDate));
  }, [startDate, snfLaunchDate]);

  const maxDate = useMemo(() => addYears(new Date(), 2), []);

  const canGoNext = useMemo(() => {
    const nextWeekStart = addWeeks(startDate, 1);
    return !isAfter(nextWeekStart, startOfDay(maxDate));
  }, [startDate, maxDate]);

  const goToNextWeek = useCallback(() => {
    if (canGoNext) {
      actions.goToNextWeek();
    }
  }, [canGoNext, actions]);

  const goToPreviousWeek = useCallback(() => {
    if (canGoPrevious) {
      actions.goToPreviousWeek();
    }
  }, [canGoPrevious, actions]);

  return {
    startDate,
    endDate,
    daysOfWeek,
    goToNextWeek,
    goToPreviousWeek,
    goToToday: actions.goToToday,
    canGoNext,
    canGoPrevious,
    isCurrentWeek,
  };
}
